import {supabase} from './supabase';

export type OrdinarySharedProfile={
 accountId:string;
 username:string|null;
 displayName:string;
 updatedAt:string|null;
};

/** Read only the signed-in user's profile from the central Ordinary record. */
export async function loadOrdinarySharedProfile():Promise<OrdinarySharedProfile>{
 const {data:auth,error:authError}=await supabase.auth.getUser();
 if(authError||!auth.user)throw new Error('Sign in to view your profile.');
 const user=auth.user;
 const fallback=String(user.user_metadata?.full_name||user.user_metadata?.display_name||
  user.user_metadata?.name||user.email?.split('@')[0]||'My account').trim().slice(0,80);
 const {data,error}=await supabase.from('ordinary_shared_profiles')
  .select('account_id,username,display_name,updated_at').eq('account_id',user.id).maybeSingle();
 if(error)throw new Error('Unable to load your profile: '+error.message);
 return {accountId:user.id,username:data?.username||null,displayName:data?.display_name||fallback,
  updatedAt:data?.updated_at||null};
}

/** Auth.uid() controls writes in the database; no client-supplied account ID. */
export async function saveOrdinarySharedProfile(username:string,displayName:string):Promise<OrdinarySharedProfile>{
 const normalized=username.trim().toLowerCase(),name=displayName.trim();
 if(normalized&&!/^[a-z][a-z0-9_]{2,29}$/.test(normalized)){
  throw new Error('Username must be 3–30 characters, starting with a letter; use letters, numbers or underscores.');
 }
 if(!name||name.length>80)throw new Error('Display name must be between 1 and 80 characters.');
 const {data,error}=await supabase.rpc('ordinary_update_shared_profile',{
  p_username:normalized||null,p_display_name:name
 });
 if(error){
  if(error.code==='23505'||/profile_username_taken/i.test(error.message))throw new Error('That username is already taken.');
  if(/profile_username_invalid_or_reserved/i.test(error.message))throw new Error('This username is unavailable. Choose a different one.');
  throw new Error(error.message);
 }
 if(!data?.accountId)throw new Error('The shared profile update was not confirmed.');
 return {accountId:String(data.accountId),username:data.username||null,
  displayName:String(data.displayName),updatedAt:data.updatedAt||null};
}

/** Header displays the configured handle, falling back to the account's display name. */
export const accountLabel=(profile:OrdinarySharedProfile|null)=>profile?.username
 ?'@'+profile.username:(profile?.displayName||'My account');
